import { ReactNode, FunctionComponent } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'src/utils/twMerge';

import CdnIcon from 'src/designSystem/CdnIcon';
import CelebrationModal, {
  CelebrationContainer,
  CelebrationEnter,
  CelebrationSkin,
} from 'src/designSystem/CelebrationModal';
import StreakWeekStrip from 'src/components/Streak/celebration/StreakWeekStrip';
import {
  GOLD_HERO_MIN,
  streakHeroTier,
} from 'src/components/Streak/page/utils/streakHeroTiers';

export type StreakCelebrationVariant = 'start' | 'milestone' | 'goal';

const PURPLE: CelebrationSkin = {
  background: 'bg-streak-celebration-purple',
  rays: 'streakPurple',
  button: 'primary',
};

const GOLD: CelebrationSkin = {
  background: 'bg-streak-celebration-gold',
  rays: 'streakGold',
  button: 'dark',
  isOnLight: true,
};

const skinOf = (
  variant: StreakCelebrationVariant,
  streakLength: number,
): CelebrationSkin =>
  variant === 'milestone' && streakLength >= GOLD_HERO_MIN ? GOLD : PURPLE;

type SentencesProps = {
  title: ReactNode;
  description: ReactNode;
  className?: string;
};

const Sentences: FunctionComponent<SentencesProps> = ({
  title,
  description,
  className,
}) => (
  <div
    className={twMerge(
      'flex w-full flex-col items-center gap-2 text-center',
      className,
    )}
  >
    <span className="text-2xl font-bold tracking-[0.24px]">{title}</span>
    <span className="text-lg font-semibold">{description}</span>
  </div>
);

type Props = {
  variant: StreakCelebrationVariant;
  streakLength: number;
  goalDays?: number;
  rewardBabz?: number;

  today?: Dayjs;
  onContinue: () => void;
  enter?: CelebrationEnter;
  container?: CelebrationContainer;
  isHeld?: boolean;
};

const StreakCelebration: FunctionComponent<Props> = ({
  variant,
  streakLength,
  goalDays,
  rewardBabz,
  today = dayjs(),
  onContinue,
  enter,
  container,
  isHeld,
}) => {
  const { t } = useTranslation();
  const tier = streakHeroTier(streakLength);
  const skin = skinOf(variant, streakLength);
  const isGold = skin === GOLD;

  return (
    <CelebrationModal
      skin={skin}
      testIds={{
        root: 'streak-celebration',
        rays: 'streak-celebration-rays',
        raysDisc: 'streak-celebration-rays-disc',
        continueButton: 'btn-streak-celebration-continue',
        closeButton: 'btn-streak-celebration-close',
      }}
      analyticsName="streak"
      isHapticOnView
      continueLabel={
        variant === 'goal'
          ? t('streak.goal.claim')
          : t('streak.celebration.continue')
      }
      onContinue={onContinue}
      enter={enter}
      container={container}
      isHeld={isHeld}
    >
      <div
        data-testid="streak-celebration-content"
        data-variant={variant}
        className={twMerge(
          'relative flex w-full flex-col items-center justify-center p-6',
          'gap-4',
          isGold ? 'text-structural-darkest' : 'text-structural-lightest',
        )}
      >
        {variant === 'goal' ? (
          <>
            <CdnIcon
              name="streak/goal-reward.svg"
              className="h-[134px] w-auto aspect-[224.06/136.16]"
            />
            <Sentences
              title={t('streak.goal.celebrationTitle', { count: goalDays ?? 0 })}
              description={t('streak.goal.celebrationDescription', {
                count: rewardBabz ?? 0,
              })}
            />
          </>
        ) : variant === 'start' ? (
          <>

            <CdnIcon
              name={tier.icon}
              className={twMerge('h-auto w-[94px]', tier.iconAspectClassName)}
            />
            <span className="text-6xl font-bold leading-none">
              {streakLength}
            </span>
            <Sentences
              title={t('streak.celebration.startTitle')}
              description={t('streak.celebration.startDescription')}
            />
          </>
        ) : (
          <>
            <CdnIcon
              name={tier.icon}
              className={twMerge(
                isGold ? 'h-[200px] w-auto' : 'h-auto w-[94px]',
                tier.iconAspectClassName,
              )}
            />
            <div className="flex flex-col items-center gap-4">
              <span className="text-6xl font-bold leading-none">
                {streakLength}
              </span>
              <span className="text-2xl font-bold tracking-[0.24px]">
                {t('streak.page.days', { count: streakLength })}
              </span>
            </div>

            <StreakWeekStrip
              today={today}
              className="-mx-6 mt-2 w-auto self-stretch"
            />
          </>
        )}
      </div>
    </CelebrationModal>
  );
};

export default StreakCelebration;
